import type { AccountSessionOut } from "../contract/api";
import { t } from "../contract/strings";
import {
  EqBarsIcon,
  Skeleton,
  SkeletonLine,
  SyncIcon,
  WarningIcon,
  cx,
} from "../ui";
import { fmtHistoryStamp, meetingTitle } from "./meeting-title";

/**
 * One line of account history, as `/v1/accounts/me/sessions` returns it, and the
 * card that draws it -- in the archive and in home's strip, which is the same
 * card on purpose: two shapes for one object is how the two screens start
 * disagreeing about what a meeting looks like.
 *
 * What the card shows: the meeting's own emoji, its title, one Gmail-style stamp,
 * and -- only when the meeting is not ready -- one small mark in the bottom-right
 * corner. The state-to-mark mapping and the open rule live here, and a Rust twin
 * in `core/crates/mf-present/src/history.rs` serves the apps; the shared fixtures
 * in `contract/fixtures/present/history/` keep the two in step.
 */
export type PastSession = AccountSessionOut;

/** The one status a card carries. A ready meeting has none -- the card is silent. */
export type HistoryMark = "live" | "working" | "failed" | "no_transcript";

/**
 * The emoji a row shows when its summary chose none: an old result, a summary that
 * has not landed, or a meeting still recording. The apps get this from the shared
 * row model; the web resolves it here, in the web's own row model.
 */
export const FALLBACK_EMOJI = "📝";

/** The meeting's emoji, or the fallback. Never empty, so every card has one tile. */
export function emojiOf(session: PastSession): string {
  return session.emoji?.trim() || FALLBACK_EMOJI;
}

/** How many circles a card draws. The rest of the room is a count, not a face. */
export const SPEAKER_CIRCLES_MAX = 3;

/**
 * One or two letters for a person. An anonymous speaker is labelled "Speaker A", so its
 * initial is the letter after the label, not an "S". One word is one letter.
 */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "";
  if (words[0].toLowerCase() === "speaker" && words[1]) return words[1][0].toUpperCase();
  return words.slice(0, 2).map((word) => word[0].toUpperCase()).join("");
}

/**
 * A colour per person, not per position: the same name lands on the same fill in every
 * card, and a rename moves them one place at most. FNV-1a, because a simpler sum puts
 * "Ravi" and "Mohini" on the same colour for ending alike.
 */
export function hueOf(name: string): number {
  let hash = 2166136261;
  for (const character of name) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) % 4;
}

/**
 * The names a row can draw, capped as the row model caps them: three, non-blank, in the
 * result's card order. The server sends at most three already; this is the client's half of
 * the same rule, so a payload that names more cannot make a taller stack.
 */
export function speakerNamesOf(session: PastSession): string[] {
  return (session.speaker_names ?? [])
    .map((name) => name.trim())
    .filter(Boolean)
    .slice(0, SPEAKER_CIRCLES_MAX);
}

export interface ParticipantStack {
  /** Up to three: initials, or two initials and the count of the people not drawn. */
  circles: string[];
  /** How many people are not drawn, or 0. */
  more: number;
}

/**
 * A ready meeting's corner: at most three circles. Three people or fewer are their
 * initials; more than three are two initials and a "+n" in the third place. Nothing for a
 * meeting that is not ready -- there is no roster until the transcript exists.
 */
export function participantsOf(session: PastSession): ParticipantStack {
  const names = speakerNamesOf(session);
  const total = session.speaker_count ?? names.length;
  if (session.state !== "complete" || names.length === 0 || total <= 0) {
    return { circles: [], more: 0 };
  }
  const shown = total > SPEAKER_CIRCLES_MAX ? 2 : Math.min(names.length, SPEAKER_CIRCLES_MAX);
  const circles = names.slice(0, shown).map(initialsOf);
  return { circles, more: Math.max(0, total - shown) };
}

/**
 * "23 Aug 2026" -- the date line the transcript export writes, and the day half of
 * credits history's stamp. Not the card's: the card assembles its own stamp, so a
 * meeting started today reads as a time. These three formatters outlived the card's
 * meta grid because other screens share them.
 */
export const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

/** `fmtDate` and a time on one line, for a transaction in credits history. */
export const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

/** "45 min", "1 h 12 min": the transcript export's duration line. Seconds are noise. */
export function fmtDuration(seconds: number): string {
  const totalMinutes = Math.round(seconds / 60);
  if (totalMinutes < 1) return t("past_session.under_a_minute");
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (!hours) return t("past_session.minutes", { minutes });
  return minutes
    ? t("past_session.hours_minutes", { hours, minutes })
    : t("past_session.hours", { hours });
}

/** The pipeline's between-states: ended, but the transcript not published yet. */
export function isWritingUp(session: PastSession): boolean {
  return session.state === "closing" || session.state === "finalising";
}

/**
 * Whether the card goes anywhere.
 *
 * Everything finished opens -- including a complete session with no transcript --
 * because the result screen is where the answer is written ("failed", "no audio
 * from your devices reached us"), and a wordless card cannot carry a sentence. A
 * recording session, and one that has not ended, have nothing to show: inert.
 */
export function isOpenable(session: PastSession): boolean {
  return session.state === "complete" || session.state === "failed" || isWritingUp(session);
}

/** The mark a session's state earns, or `null` for ready. */
export function markOf(session: PastSession): HistoryMark | null {
  if (session.state === "complete") {
    // No audio from this account's devices ever reached us: the same red as a
    // failure, because from the user's seat it is one -- the meeting is gone.
    return session.readable ? null : "no_transcript";
  }
  if (session.state === "open" || session.state === "recording") return "live";
  if (isWritingUp(session)) return "working";
  if (session.state === "failed") return "failed";
  // A state this client has no word for: nothing honest to draw, and the row is
  // inert either way. It can only appear when the client is older than the server.
  return null;
}

/**
 * Open a session's transcript.
 *
 * No pre-flight, deliberately: for every clickable state the result screen is the
 * better place to be whatever the answer -- it renders the transcript, says it is
 * still being written up and polls, or reports the refusal with its own status
 * line. Duplicating any of that here would be two places agreeing to stay in step.
 */
export function openPastSession(session: PastSession, navigate: (path: string) => void): void {
  navigate(`/session/${session.session_id}/result`);
}

/**
 * The mark, in the card's bottom-right corner. It is the only status the card
 * carries, so it is named for a screen reader rather than hidden -- the two red
 * cases share a glyph and split by their name. The amber glyph turns and the live
 * bars breathe; both hold still under `prefers-reduced-motion`, where the colour
 * and the glyph still say it.
 */
function Mark({ mark }: { mark: HistoryMark }) {
  const box = "absolute right-pad bottom-pad flex";
  if (mark === "live") {
    return (
      <span
        className={cx(box, "size-icon-xs text-accent")}
        role="img"
        aria-label={t("past_session.in_progress")}
      >
        <EqBarsIcon className="history-bars size-full" />
      </span>
    );
  }
  const working = mark === "working";
  const Icon = working ? SyncIcon : WarningIcon;
  return (
    <span
      className={cx(
        box,
        "size-icon-sm",
        working
          ? "text-warn-ink animate-status-turn motion-reduce:animate-none"
          : "text-danger"
      )}
      role="img"
      aria-label={
        working
          ? t("state.processing_label")
          : mark === "failed"
            ? t("past_session.failed")
            : t("past_session.no_transcript")
      }
    >
      <Icon className="size-full" />
    </span>
  );
}

/**
 * The card's box. Top and bottom padding are the same value, so the emoji tile has equal
 * space above and below and reads as centred; the clock's top inset and the mark's bottom
 * inset are that same value, which puts the clock on the tile's top edge and the mark on
 * its bottom edge.
 */
const CARD_BOX =
  "relative grid w-full grid-cols-[auto_minmax(0,1fr)] items-stretch gap-x-gutter " +
  "bg-surface border border-line rounded-card pt-pad pr-pad pb-pad pl-input-x text-left";

/** The avatar fills, in the order `hueOf` indexes them. */
const AVATAR_FILLS = ["avatar-accent", "avatar-rust", "avatar-mist", "avatar-slate"] as const;

/**
 * A ready meeting's people, in the corner the mark would use. One circle per person, later
 * ones on top and ringed in the card's own surface; the "+n" is a count, so it keeps the
 * quiet surface rather than a colour.
 */
function Participants({ session }: { session: PastSession }) {
  const { circles, more } = participantsOf(session);
  if (!circles.length) return null;
  const names = speakerNamesOf(session);
  const taken = new Set<number>();
  return (
    <span
      className="absolute right-pad bottom-pad flex items-center"
      role="img"
      aria-label={t("past_session.speakers", { count: session.speaker_count ?? names.length })}
    >
      {circles.map((initials, index) => {
        const name = names[index] ?? initials;
        let hue = hueOf(name);
        while (taken.has(hue) && taken.size < AVATAR_FILLS.length) hue = (hue + 1) % AVATAR_FILLS.length;
        taken.add(hue);
        return (
          <span
            key={`${name}-${index}`}
            className={cx(
              "size-icon rounded-pill ring-2 ring-surface flex flex-none items-center justify-center",
              "font-code text-count font-semibold uppercase leading-none",
              index > 0 && "-ml-tight",
              AVATAR_FILLS[hue]
            )}
          >
            {initials}
          </span>
        );
      })}
      {more > 0 ? (
        <span
          className={cx(
            "size-icon rounded-pill ring-2 ring-surface flex flex-none items-center justify-center",
            "bg-surface-2 text-ink-soft font-code text-count font-semibold leading-none",
            "-ml-tight"
          )}
        >
          +{more}
        </span>
      ) : null}
    </span>
  );
}

/**
 * The archive card: the meeting's emoji on a tinted tile, its title centred beside it, the
 * Gmail stamp on the card's top row and — only when something needs saying — either the
 * mark or, for a ready meeting, the people who were in it. Openable cards are buttons; a
 * recording one is not, because there is nothing to open yet.
 */
export function PastSessionCard({
  session,
  onOpen,
  now,
}: {
  session: PastSession;
  onOpen: (session: PastSession) => void;
  /** The moment "today" is judged against. Tests pin it; production passes none. */
  now?: Date;
}) {
  const mark = markOf(session);
  const openable = isOpenable(session);
  const emoji = emojiOf(session);
  const contents = (
    <>
      <span
        aria-hidden="true"
        className="size-tile rounded-squircle bg-surface-2 flex flex-none self-center items-center justify-center text-emoji leading-none"
      >
        {emoji}
      </span>
      {/* The body fills the card's row, so the title inside it has the whole height to
          centre in while the clock stays on the top row. */}
      <span className="grid min-w-0">
        <span className="grid h-full grid-cols-[minmax(0,1fr)_auto] items-center gap-x-tight">
          {/* The title is set in the display face but not uppercased: it is the user's
              own words, and mono caps would shout and misrepresent them. It wraps to two
              lines and is clipped there. */}
          <span className="min-w-0 self-center line-clamp-2 font-title text-person font-semibold tracking-headline leading-row text-ink">
            {meetingTitle(session.title, session.created_at)}
          </span>
          <span className="self-start font-code text-count text-ink-soft">
            {fmtHistoryStamp(session.created_at, { now })}
          </span>
        </span>
      </span>
      {mark ? <Mark mark={mark} /> : null}
      {mark ? null : <Participants session={session} />}
    </>
  );
  return openable ? (
    <button
      type="button"
      onClick={() => onOpen(session)}
      className={cx(
        CARD_BOX,
        "button-transition enabled:hover:border-line-strong enabled:active:button-press"
      )}
    >
      {contents}
    </button>
  ) : (
    <div className={CARD_BOX}>{contents}</div>
  );
}

/**
 * `PastSessionCard` while the page loads (#1043): the same box, the emoji tile,
 * the title line and the stamp line, as static blocks.
 */
export function PastSessionCardSkeleton() {
  return (
    <span className={CARD_BOX}>
      <Skeleton className="size-tile rounded-squircle self-center" />
      {/* The same head as the card: one line beside the clock's block, both centred. */}
      <span className="grid min-w-0">
        <span className="grid h-full grid-cols-[minmax(0,1fr)_auto] items-center gap-x-tight">
          <SkeletonLine size="person" width="w-1/2" className="self-center" />
          <Skeleton className="self-start h-icon-sm w-tile-sm rounded-pill" />
        </span>
      </span>
    </span>
  );
}
